import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { OrgDashboardProvider, useOrgDashboard } from "./context/OrgDashboardContext";
import { OrgHeader, OrgSidebar, type TabId } from "./components";
import { exportEmployeeReportApiV1B2bSlugExportReportGet } from "@/src/api/sdk.gen";
import {
  OverviewPage,
  MembersPage,
  DocumentsPage,
  SettingsPage,
  CoursesPage,
  IntelligencePage,
  WorkspacesPage,
  BillingPage,
} from "./subpages";
import { LearningRulesTab } from "./LearningRulesTab";

function B2BOrgDashboardContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Tab & Workspace synchronization
  const activeTab = (searchParams.get("tab") as TabId) || "overview";
  const activeWsSlug = searchParams.get("ws");

  const handleSelectTab = (tab: TabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "overview") {
          next.delete("tab");
        } else {
          next.set("tab", tab);
        }
        // Always reset workspace detail drill-down when navigating tabs to show list view by default
        next.delete("ws");
        return next;
      },
      { replace: false }
    );
  };

  const handleSelectWorkspaceDetail = (wsSlug: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (wsSlug) {
          next.set("ws", wsSlug);
        } else {
          next.delete("ws");
        }
        return next;
      },
      { replace: false }
    );
  };

  // Sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Consume Context
  const {
    slug,
    stats,
    loadingStats,
    loadStats,
    workspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    detailedWorkspace,
    loadingWorkspaceDetail,
    loadWorkspaceDetail,
    loadWorkspaces,
    members,
    loadMembers,
    invitations,
    loadInvites,
    documents,
    loadDocuments,
    creditsSummary,
    loadCredits,
  } = useOrgDashboard();

  // Tab-driven lazy data fetching: only load data needed for active tab
  useEffect(() => {
    if (!slug) return;
    if (activeTab === "workspaces") {
      void loadWorkspaces();
      if (activeWsSlug) {
        void loadWorkspaceDetail(activeWsSlug);
      }
    } else if (activeTab === "members") {
      void loadMembers();
      void loadInvites();
      void loadWorkspaces();
    } else if (activeTab === "documents") {
      void loadDocuments();
    } else if (activeTab === "courses") {
      void loadWorkspaces();
      void loadDocuments();
      void loadCredits();
    } else if (activeTab === "billing") {
      void loadCredits(true);
    }
  }, [
    activeTab,
    activeWsSlug,
    slug,
    loadWorkspaces,
    loadWorkspaceDetail,
    loadMembers,
    loadInvites,
    loadDocuments,
    loadCredits,
  ]);

  const [isExportingReport, setIsExportingReport] = useState(false);

  const handleExportCSV = async () => {
    if (!slug || isExportingReport) return;
    setIsExportingReport(true);
    try {
      const res = await exportEmployeeReportApiV1B2bSlugExportReportGet({
        path: { slug },
        parseAs: "blob",
      } as any);

      if (res.error || !res.data) {
        throw new Error("Failed to export organization report");
      }

      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([String(res.data)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `${slug}_learning_report_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export report:", err);
      alert("Could not export report. Please try again.");
    } finally {
      setIsExportingReport(false);
    }
  };

  if (loadingStats) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4" />
        <p className="text-slate-600 font-medium">Loading organization dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <AlertCircle className="w-12 h-12 text-rose-500 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Workspace Not Found</h2>
        <p className="text-slate-500 text-sm mb-6">Could not load details for this organization.</p>
        <button
          onClick={() => navigate("/courses")}
          className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-semibold shadow-xs cursor-pointer"
        >
          Go to Courses
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col">
      {/* Sticky Top Header */}
      <OrgHeader
        stats={stats}
        workspaces={workspaces}
        selectedWorkspace={selectedWorkspace}
        onSelectWorkspace={(ws) => {
          setSelectedWorkspace(ws);
          if (activeWsSlug || activeTab === "workspaces") {
            handleSelectWorkspaceDetail(ws.slug);
          }
        }}
        onExportCSV={handleExportCSV}
        isExporting={isExportingReport}
        onToggleMobileNav={() => setIsMobileNavOpen((prev) => !prev)}
        isMobileNavOpen={isMobileNavOpen}
        creditsSummary={creditsSummary}
        onOpenBilling={() => handleSelectTab("billing")}
      />

      {/* Main Body with Sidebar + Subpage View */}
      <div
        className={`flex-1 flex w-full transition-[padding] duration-300 ${
          isSidebarCollapsed ? "lg:pl-20" : "lg:pl-64"
        }`}
      >
        {/* Navigation Sidebar */}
        <OrgSidebar
          activeTab={activeTab}
          onSelectTab={handleSelectTab}
          stats={stats}
          workspaces={workspaces}
          members={members}
          invitations={invitations}
          documents={documents}
          creditsSummary={creditsSummary}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
          isMobileOpen={isMobileNavOpen}
          onCloseMobile={() => setIsMobileNavOpen(false)}
        />

        {/* Content Area */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Quick Mobile Tab Navigator (visible below lg) */}
          <div className="flex lg:hidden items-center gap-1.5 overflow-x-auto pb-3 mb-6 no-scrollbar border-b border-slate-200">
            {[
              { id: "overview", label: "Overview" },
              {
                id: "members",
                label: `Members (${
                  members.length > 0 || invitations.length > 0
                    ? members.length + invitations.length
                    : stats.total_members || 0
                })`,
              },
              { id: "workspaces", label: "Workspaces" },
              { id: "courses", label: "Courses" },
              { id: "intelligence", label: "AI Intelligence" },
              { id: "assignments", label: "Learning Rules" },
              { id: "documents", label: "Knowledge" },
              { id: "billing", label: "Billing & Credits" },
              { id: "settings", label: "Settings" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleSelectTab(tab.id as TabId)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white shadow-2xs font-bold"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Subpage Router */}
          <div className="animate-in fade-in duration-200">
            {activeTab === "overview" && (
              <OverviewPage
                slug={slug}
                stats={stats}
                onNavigateTab={(tab) => handleSelectTab(tab as TabId)}
              />
            )}

            {activeTab === "assignments" && (
              <LearningRulesTab slug={slug} stats={stats} />
            )}

            {activeTab === "intelligence" && (
              <IntelligencePage
                slug={slug}
                onNavigateToAssignments={() => handleSelectTab("assignments")}
              />
            )}

            {activeTab === "members" && (
              <MembersPage
                slug={slug}
                stats={stats}
                members={members}
                invitations={invitations}
                workspaces={workspaces}
                loadMembers={loadMembers}
                loadInvites={loadInvites}
                loadStats={loadStats}
              />
            )}

            {activeTab === "workspaces" && (
              <WorkspacesPage
                slug={slug}
                stats={stats}
                workspaces={workspaces}
                activeWorkspaceSlug={activeWsSlug}
                onSelectWorkspaceForDetail={handleSelectWorkspaceDetail}
                detailedWorkspace={detailedWorkspace}
                loadingWorkspaceDetail={loadingWorkspaceDetail}
                members={members}
                loadWorkspaces={loadWorkspaces}
                loadWorkspaceDetail={loadWorkspaceDetail}
                loadMembers={loadMembers}
                selectedWorkspace={selectedWorkspace}
                setSelectedWorkspace={setSelectedWorkspace}
              />
            )}

            {activeTab === "courses" && (
              <CoursesPage courses={stats.sponsored_courses} />
            )}

            {activeTab === "documents" && (
              <DocumentsPage
                slug={slug}
                documents={documents}
                loadDocuments={loadDocuments}
              />
            )}

            {activeTab === "billing" && (
              <BillingPage />
            )}

            {activeTab === "settings" && (
              <SettingsPage
                slug={slug}
                stats={stats}
                onStatsUpdated={() => void loadStats(true)}
                loadData={() => loadStats(true)}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default function B2BOrgDashboardPage() {
  const { slug = "" } = useParams<{ slug: string }>();

  return (
    <OrgDashboardProvider slug={slug}>
      <B2BOrgDashboardContent />
    </OrgDashboardProvider>
  );
}
